import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../hooks/ProjectContext';
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, Container,
  Divider, Grid, LinearProgress, Stack, Typography,
} from '@mui/material';
import Iconify from '../../components/iconify';

// ---------------------------------------------------------------------------
// Phase definitions
// ---------------------------------------------------------------------------
const PHASES = [
  {
    key: 'feasibility', label: 'Feasibility', path: '/project/feasibility',
    icon: 'eva:checkmark-circle-2-fill', color: '#2196f3',
    desc: 'Carbon potential & financial viability assessment',
    steps: ['Boundary & Area', 'Eligibility', 'Carbon Potential', 'Additionality', 'Financial Model'],
  },
  {
    key: 'baseline', label: 'Baseline', path: '/project/baseline',
    icon: 'eva:activity-fill', color: '#ff9800',
    desc: 'Stratification, sampling plan & SOC measurement',
    steps: ['Stratification', 'Sampling Plan', 'Baseline SOC', 'Baseline Emissions', 'PD + ERR-PD'],
  },
  {
    key: 'monitoring', label: 'Monitoring', path: '/project/monitoring',
    icon: 'eva:bar-chart-fill', color: '#4caf50',
    desc: 'Annual monitoring, ERR calculation & credit issuance',
    steps: ['SOC Re-measurement', 'Emissions', 'SDG Assessment', 'Incidents', 'ERR Calculation', 'MR + ERR-MR'],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function ProjectHomePage() {
  const navigate = useNavigate();
  const { selectedProject, selectedSite } = useProject();
  const [realProject, setRealProject] = useState(null);
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);

  const projectId = selectedProject?.code || '';
  const siteLabel = selectedSite?.code || selectedSite?.name || null;

  // Fetch project detail
  useEffect(() => {
    if (!selectedProject?.id) { setRealProject(null); return; }
    fetch(`/api/access/projects/${selectedProject.id}/detail`)
      .then(r => r.json())
      .then(j => { if (j.ok) setRealProject(j.project); })
      .catch(() => {});
  }, [selectedProject?.id]);

  // Fetch lightweight stats (artifacts count, evidence count, annotations count)
  useEffect(() => {
    if (!projectId) { setStats(null); return; }
    const params = new URLSearchParams({ project: projectId });
    if (selectedSite?.code) params.set('site', selectedSite.code);
    Promise.all([
      fetch(`/v1/stratification/artifacts?${params}`).then(r => r.ok ? r.json() : []),
      fetch(`/v1/evidence?${params}`).then(r => r.ok ? r.json() : []),
    ]).then(([artifacts, evidence]) => {
      setStats({
        artifacts: Array.isArray(artifacts) ? artifacts.length : 0,
        evidence: Array.isArray(evidence) ? evidence.length : 0,
        strata: Array.isArray(artifacts) ? artifacts.filter(a => a.subtype === 'strata').length : 0,
        samples: Array.isArray(artifacts) ? artifacts.filter(a => a.tool === 'sampling-points').length : 0,
        rasters: Array.isArray(artifacts) ? artifacts.filter(a => a.subtype === 'stack').length : 0,
      });
    }).catch(() => setStats(null));
  }, [projectId, selectedSite]);

  // Fetch news
  useEffect(() => {
    fetch('/v1/news?limit=3')
      .then(r => r.ok ? r.json() : [])
      .then(items => setNews(Array.isArray(items) ? items : []))
      .catch(() => setNews([]));
  }, []);

  if (!selectedProject) {
    return (
      <>
        <Helmet><title>Project | TERRRA</title></Helmet>
        <Container maxWidth="lg">
          <Alert severity="info" sx={{ mt: 3 }}>Select a project from the header to get started.</Alert>
        </Container>
      </>
    );
  }

  const isAllSites = !selectedSite;
  const displayName = isAllSites
    ? (realProject?.name || selectedProject?.name || projectId)
    : (selectedSite?.name || selectedSite?.code || '');
  const projDesc = isAllSites ? (realProject?.description || null) : null;
  const areaHa = isAllSites
    ? (realProject?.area_ha_kml || realProject?.area_ha_estimated || null)
    : (stats ? `~${stats.artifacts} artifacts` : null); // site-level area from stats context
  const startDate = realProject?.start_date ? fmtDate(realProject.start_date) : null;
  // For site view, compute area from boundary polygon count
  const siteAreaLabel = !isAllSites && stats ? null : null; // area calculated from boundary if available

  return (
    <>
      <Helmet><title>{displayName} | TERRRA</title></Helmet>
      <Container maxWidth="lg" sx={{ py: 2 }}>

        {/* --- Title area: project + site aware --- */}
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="h4">{displayName}</Typography>
          {!isAllSites && (
            <Chip label={selectedProject?.name || projectId} size="small" variant="outlined" />
          )}
        </Stack>
        {projDesc && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 720 }}>
            {projDesc}
          </Typography>
        )}
        <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
          {isAllSites && areaHa && (
            <Chip size="small" variant="outlined" label={`Area: ${Number(areaHa).toLocaleString()} ha`}
              sx={{ borderColor: 'divider', fontSize: 11 }} />
          )}
          {isAllSites && startDate && (
            <Chip size="small" variant="outlined" label={`Start: ${startDate}`}
              sx={{ borderColor: 'divider', fontSize: 11 }} />
          )}
          {!isAllSites && selectedSite?.phase_inclusion && (
            <Chip size="small" variant="outlined"
              label={`Phase: ${selectedSite.phase_inclusion}`}
              color={selectedSite.phase_inclusion === 'current' ? 'primary' : 'default'}
              sx={{ fontSize: 11 }} />
          )}
          <Chip size="small" variant="outlined" label="VM0042 v2.2 (ALM)"
            sx={{ borderColor: 'divider', fontSize: 11 }} />
          {isAllSites && (
            <>
              <Chip size="small" variant="outlined" label="Crediting: 25 years"
                sx={{ borderColor: 'divider', fontSize: 11 }} />
              {areaHa && (
                <Chip size="small" variant="outlined"
                  label={`Est. vintage: ${Math.round(Number(areaHa) * 3.2).toLocaleString()} tCO2e/yr`}
                  sx={{ borderColor: 'divider', fontSize: 11 }}
                  title="Rough estimate: area × 3.2 tCO2e/ha/yr (IPCC Tier 1 default for improved grassland)" />
              )}
              <Chip size="small" variant="outlined" label="Buffer: 18% (est.)"
                sx={{ borderColor: 'divider', fontSize: 11 }} />
              {areaHa && (
                <Chip size="small" variant="outlined"
                  label={`Net credits: ~${Math.round(Number(areaHa) * 3.2 * 0.82).toLocaleString()} VCUs/yr`}
                  sx={{ borderColor: 'divider', fontSize: 11 }} />
              )}
            </>
          )}
        </Stack>

        {/* --- Urgent actions --- */}
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => navigate('/project/feasibility')}>Go</Button>}>
            <Typography variant="subtitle2">Feasibility assessment not started</Typography>
            <Typography variant="body2">Complete the feasibility pipeline to evaluate project viability.</Typography>
          </Alert>
          {!areaHa && (
            <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => navigate('/project/site-visit')}>Upload</Button>}>
              <Typography variant="subtitle2">No boundary uploaded</Typography>
              <Typography variant="body2">Upload a site boundary (KML) to enable area calculations and stratification.</Typography>
            </Alert>
          )}
        </Stack>

        {/* --- Industry news --- */}
        {news.length > 0 && (
          <Stack spacing={0.75} sx={{ mb: 3 }}>
            {news.map(item => (
              <Alert key={item.id || item.link} severity="info" variant="outlined"
                icon={<Iconify icon="eva:globe-2-outline" sx={{ width: 20, height: 20 }} />}
                sx={{ py: 0.25, '& .MuiAlert-message': { py: 0.5 } }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip label={item.source} size="small" sx={{ fontSize: 10, height: 18 }} />
                  <Typography variant="body2" component="a" href={item.link} target="_blank" rel="noopener"
                    sx={{ color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    {item.title}
                  </Typography>
                </Stack>
              </Alert>
            ))}
          </Stack>
        )}

        {/* --- Phase cards --- */}
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Project Phases
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {PHASES.map((phase, pi) => (
            <Grid item xs={12} md={4} key={phase.key}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea onClick={() => navigate(phase.path)} sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        bgcolor: phase.color + '18',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Iconify icon={phase.icon} sx={{ width: 20, height: 20, color: phase.color }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>{phase.label}</Typography>
                      </Box>
                      <Chip
                        label={pi === 0 ? 'Active' : 'Upcoming'}
                        size="small"
                        color={pi === 0 ? 'primary' : 'default'}
                        variant={pi === 0 ? 'filled' : 'outlined'}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{phase.desc}</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={0}
                      sx={{ height: 4, borderRadius: 2, mb: 1,
                        '& .MuiLinearProgress-bar': { bgcolor: phase.color },
                        bgcolor: phase.color + '20',
                      }}
                    />
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {phase.steps.map(s => (
                        <Chip key={s} label={s} size="small" variant="outlined"
                          sx={{ fontSize: 10, height: 20, borderColor: 'divider' }} />
                      ))}
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* --- Quick stats + links --- */}
        <Grid container spacing={2}>
          {/* Left: stats */}
          <Grid item xs={12} md={8}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {isAllSites ? 'Project Overview' : `Site Overview — ${selectedSite?.code || selectedSite?.name}`}
            </Typography>
            <Grid container spacing={2}>
              {[
                { label: 'Raster Layers', value: stats?.rasters ?? '—', icon: 'eva:image-outline', path: '/project/library' },
                { label: 'Strata Runs', value: stats?.strata ?? '—', icon: 'eva:layers-outline', path: '/project/stratification' },
                { label: 'Sampling Runs', value: stats?.samples ?? '—', icon: 'eva:pin-outline', path: '/project/sampling' },
                { label: 'Field Evidence', value: stats?.evidence ?? '—', icon: 'eva:camera-outline', path: '/project/capture' },
              ].map(s => (
                <Grid item xs={6} sm={3} key={s.label}>
                  <Card variant="outlined" sx={{ textAlign: 'center', cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                    onClick={() => navigate(s.path)}>
                    <CardContent sx={{ py: 2 }}>
                      <Iconify icon={s.icon} sx={{ width: 28, height: 28, color: 'text.secondary', mb: 0.5 }} />
                      <Typography variant="h5">{s.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Right: quick links */}
          <Grid item xs={12} md={4}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Quick Links
            </Typography>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5 }}>
                <Stack divider={<Divider />} spacing={0}>
                  {[
                    { label: 'Feasibility Assessment', path: '/project/feasibility', icon: 'eva:checkmark-circle-2-outline' },
                    { label: 'Map & Site Viewer', path: '/project/site-visit', icon: 'eva:map-outline' },
                    { label: 'Stratification Pipeline', path: '/project/stratification', icon: 'eva:layers-outline' },
                    { label: 'Library & Artifacts', path: '/project/library', icon: 'eva:folder-outline' },
                  ].map(link => (
                    <Box key={link.label} sx={{ py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, px: 1, mx: -1, borderRadius: 0.5 }}
                      onClick={() => navigate(link.path)}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Iconify icon={link.icon} sx={{ width: 18, height: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">{link.label}</Typography>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      </Container>
    </>
  );
}
